import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import {
  rxResource,
  takeUntilDestroyed,
  toObservable,
  toSignal,
} from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DatePickerModule } from 'primeng/datepicker';
import { InputTextModule } from 'primeng/inputtext';
import { Message } from 'primeng/message';
import { TextareaModule } from 'primeng/textarea';
import { Toast } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { catchError, filter, of, take } from 'rxjs';
import { CompilingApiService } from '../../../api/compiling-api.service';
import { PefReportParameters } from '../../../api/interfaces';
import { AiButtonComponent } from '../../../components/ai-button/ai-button.component';
import { PefStateService } from '../../../services/compiling/state/pef-state.service';
import { TenderContextService } from '../../../services/compiling/state/tender-context.service';
import { AnswerPermissionsService } from '../../../services/compiling/state/answer-permissions.service';
import { EVALUATION_CAPABILITIES } from '../../../services/evaluation-capabilities';

@Component({
  selector: 'lib-pef-report',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <p-toast></p-toast>
    <div class="flex flex-col gap-6">
      @if (reportResource.hasValue() && reportResource.value() !== null) {
        <div
          class="flex items-center justify-between rounded-2xl border border-green-200 bg-green-50 p-4"
        >
          <div>
            <p class="font-semibold text-green-800">Report già generato</p>
            <p class="text-sm text-green-700">
              @if (permissions.canEditEconomicOffer()) {
                Puoi scaricarlo o generarne uno nuovo compilando il modulo.
              } @else {
                Puoi scaricarlo.
              }
            </p>
          </div>
          @if (capabilities.canDownloadPefReport) {
            <p-button
              label="Scarica"
              icon="pi pi-download"
              severity="success"
              (onClick)="download()"
            />
          }
        </div>
      }

      <!-- 404 is swallowed via catchError in the stream; this shows only for unexpected errors -->
      @if (reportResource.error()) {
        <p-message
          severity="warn"
          text="Impossibile verificare la presenza di un report esistente."
        />
      }

      @if (!permissions.canEditEconomicOffer()) {
        @if (!(reportResource.hasValue() && reportResource.value() !== null)) {
          <div
            class="flex items-center gap-2 rounded-2xl border border-gray-200 bg-slate-50 p-4 text-sm text-slate-500"
          >
            <i class="pi pi-info-circle"></i>
            Nessun report generato da chi compila l'offerta economica.
          </div>
        }
      } @else {
      <div class="rounded-2xl border border-gray-200 bg-white p-6">
        <h2 class="mb-4 text-lg font-semibold">
          Genera Report Offerta Economica
        </h2>
        <form [formGroup]="form" class="flex flex-col gap-4">
          <div class="flex flex-col gap-1">
            <label for="nomeComune" class="text-sm font-medium"
              >Nome Comune</label
            >
            <input
              pInputText
              id="nomeComune"
              formControlName="nomeComune"
              class="w-full"
            />
          </div>

          <div class="flex flex-col gap-1">
            <label for="nomeBando" class="text-sm font-medium">
              Nome Bando / Riferimento Atto
            </label>
            <input
              pInputText
              id="nomeBando"
              formControlName="nomeBandoRifAtto"
              class="w-full"
            />
          </div>

          <div class="flex flex-col gap-1">
            <label for="nomeConcessionario" class="text-sm font-medium">
              Nome Concessionario
            </label>
            <input
              pInputText
              id="nomeConcessionario"
              formControlName="nomeConcessionario"
              class="w-full"
            />
          </div>

          <div class="flex flex-col gap-1">
            <label for="data" class="text-sm font-medium">Data</label>
            <p-datepicker
              [autofocus]="false"
              inputId="data"
              formControlName="data"
              dateFormat="dd/mm/yy"
              appendTo="body"
              [readonlyInput]="true"
              fluid
            />
          </div>

          <div class="flex items-center gap-2">
            <p-checkbox
              formControlName="includeSenzaRicorsoContributi"
              [binary]="true"
              inputId="senzaContributi"
            />
            <label for="senzaContributi" class="text-sm">
              Includi scenario senza ricorso a contributi
            </label>
          </div>

          <div class="flex items-center gap-2">
            <p-checkbox
              formControlName="includeDescrizioneIniziativa"
              [binary]="true"
              inputId="includeDescrizione"
            />
            <label for="includeDescrizione" class="text-sm">
              Includi descrizione dell'iniziativa
            </label>
          </div>

          @if (includeDescrizione()) {
            <div class="flex flex-col gap-1">
              <div class="flex items-center justify-between">
                <label for="descrizione" class="text-sm font-medium">
                  Descrizione dell'iniziativa
                </label>
                <lib-ai-button
                  [rounded]="true"
                  tooltip="Genera con AI"
                  [loading]="generatingDescrizione()"
                  (action)="generateDescrizione()"
                />
              </div>
              <textarea
                pTextarea
                id="descrizione"
                formControlName="descrizioneIniziativaTesto"
                rows="4"
                class="w-full"
              ></textarea>
            </div>
          }

          <div class="flex justify-end">
            <p-button
              label="Genera Report"
              icon="pi pi-file-export"
              [loading]="generating()"
              [disabled]="generating()"
              (onClick)="generate()"
            />
          </div>
        </form>
      </div>
      }
    </div>
  `,
  imports: [
    AiButtonComponent,
    ReactiveFormsModule,
    ButtonModule,
    CheckboxModule,
    DatePickerModule,
    InputTextModule,
    Message,
    TextareaModule,
    Toast,
    TooltipModule,
  ],
})
export class PefReportComponent {
  private readonly compilingApiService = inject(CompilingApiService);
  private readonly pefState = inject(PefStateService);
  private readonly tenderContext = inject(TenderContextService);
  private readonly messageService = inject(MessageService);
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  protected readonly capabilities = inject(EVALUATION_CAPABILITIES);
  protected readonly permissions = inject(AnswerPermissionsService);

  protected readonly generating = signal(false);
  protected readonly generatingDescrizione = signal(false);

  protected readonly form = this.buildForm();

  protected readonly includeDescrizione = toSignal(
    this.form.controls.includeDescrizioneIniziativa.valueChanges,
    {
      initialValue:
        this.form.controls.includeDescrizioneIniziativa.value ?? true,
    },
  );

  protected readonly reportResource = rxResource({
    params: () => ({
      tenderFormAnswerId: this.tenderContext.tenderFormAnswer()?.id ?? '',
    }),
    stream: ({ params }) => {
      if (!params.tenderFormAnswerId) return of(null);
      return this.compilingApiService
        .getPefReportDownloadLink(params.tenderFormAnswerId)
        .pipe(
          catchError((err) => {
            if (err?.status === 404) return of(null);
            throw err;
          }),
        );
    },
  });

  constructor() {
    toObservable(this.pefState.pefReportParameters)
      .pipe(
        filter((saved): saved is PefReportParameters => saved !== null),
        take(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((saved) => {
        this.form.patchValue({
          nomeComune: saved.nomeComune,
          nomeBandoRifAtto: saved.nomeBandoRifAtto,
          nomeConcessionario: saved.nomeConcessionario,
          data: saved.data ? this.parseDate(saved.data) : new Date(),
          includeSenzaRicorsoContributi: saved.includeSenzaRicorsoContributi,
          includeDescrizioneIniziativa: saved.includeDescrizioneIniziativa,
          descrizioneIniziativaTesto: saved.descrizioneIniziativaTesto ?? '',
        });
      });

    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const v = this.form.getRawValue();
        this.pefState.pefReportParameters.set({
          nomeComune: v.nomeComune ?? '',
          nomeBandoRifAtto: v.nomeBandoRifAtto ?? '',
          nomeConcessionario: v.nomeConcessionario ?? '',
          data: v.data ? this.formatDate(v.data) : '',
          includeSenzaRicorsoContributi:
            v.includeSenzaRicorsoContributi ?? false,
          includeDescrizioneIniziativa: v.includeDescrizioneIniziativa ?? true,
          descrizioneIniziativaTesto: v.includeDescrizioneIniziativa
            ? (v.descrizioneIniziativaTesto ?? '')
            : null,
        });
      });
  }

  protected generate(): void {
    const v = this.form.getRawValue();
    const reportParams: PefReportParameters = {
      nomeComune: v.nomeComune ?? '',
      nomeBandoRifAtto: v.nomeBandoRifAtto ?? '',
      nomeConcessionario: v.nomeConcessionario ?? '',
      data: v.data ? this.formatDate(v.data) : '',
      includeSenzaRicorsoContributi: v.includeSenzaRicorsoContributi ?? false,
      includeDescrizioneIniziativa: v.includeDescrizioneIniziativa ?? true,
      descrizioneIniziativaTesto: v.includeDescrizioneIniziativa
        ? (v.descrizioneIniziativaTesto ?? '')
        : null,
    };

    this.generating.set(true);
    this.compilingApiService
      .generatePefReport(
        this.tenderContext.tenderFormAnswer()?.id ?? '',
        reportParams,
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.reportResource.reload();
          this.messageService.add({
            severity: 'success',
            summary: 'Report Generato',
            detail: 'Il report è stato generato con successo.',
            life: 3000,
          });
          this.generating.set(false);
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail:
              'Si è verificato un errore durante la generazione del report.',
            life: 3000,
          });
          this.generating.set(false);
        },
      });
  }

  protected generateDescrizione(): void {
    const id = this.tenderContext.tenderFormAnswer()?.id ?? '';
    this.generatingDescrizione.set(true);
    this.compilingApiService
      .generateDescrizione(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ descrizioneIniziativaTesto }) => {
          this.form.controls.descrizioneIniziativaTesto.setValue(
            descrizioneIniziativaTesto,
          );
          this.generatingDescrizione.set(false);
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: 'Impossibile generare la descrizione.',
            life: 3000,
          });
          this.generatingDescrizione.set(false);
        },
      });
  }

  protected download(): void {
    const url = this.reportResource.value()?.url;
    if (url) {
      window.open(url, '_blank');
    } else {
      console.warn('PefReportComponent: download triggered but url is missing');
    }
  }

  private buildForm() {
    const saved = this.pefState.pefReportParameters();
    return this.fb.group({
      nomeComune: [
        saved?.nomeComune ??
          this.tenderContext.tenderInfo()?.contractingAuthority ??
          '',
        Validators.required,
      ],
      nomeBandoRifAtto: [
        saved?.nomeBandoRifAtto ??
          this.tenderContext.tenderInfo()?.title ??
          '',
        Validators.required,
      ],
      nomeConcessionario: [
        saved?.nomeConcessionario ?? this.buildConcessionario(),
        Validators.required,
      ],
      data: [
        saved?.data ? this.parseDate(saved.data) : new Date(),
        Validators.required,
      ],
      includeSenzaRicorsoContributi: [
        saved?.includeSenzaRicorsoContributi ?? false,
      ],
      includeDescrizioneIniziativa: [
        saved?.includeDescrizioneIniziativa ?? true,
      ],
      descrizioneIniziativaTesto: [saved?.descrizioneIniziativaTesto ?? ''],
    });
  }

  private buildConcessionario(): string {
    const form = this.tenderContext.applicantForm.value;
    const isCompany = (form['applicantType'] as string) === 'Company';
    if (isCompany) {
      return (form['applicantCompanyName'] as string) ?? '';
    }
    const first = (form['applicantFirstName'] as string) ?? '';
    const last = (form['applicantLastName'] as string) ?? '';
    return `${first} ${last}`.trim();
  }

  private formatDate(date: Date): string {
    return date.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }

  private parseDate(dateStr: string): Date {
    const [day, month, year] = dateStr.split('/').map(Number);
    return new Date(year, month - 1, day);
  }
}
